// The leaf messages module, not the feature barrel: the barrel pulls
// useCopySettings and the flags import graph into every editor kit.
import { emphasisSuppressionMessages } from '@/features/copySettings/emphasis-suppression.messages';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { toast } from '@heidi/design-system/toast';
import { formatMessageOutsideReact } from '@heidi/runtime/i18n';
import { Extension, type Editor } from '@tiptap/react';

type SuppressedMark = 'bold' | 'italic' | 'underline';

export interface EmphasisShortcutGateStorage {
  /**
   * Mirrors the "Bold & italics" copy setting for the NOTE editor: while
   * true, the formatting shortcuts below are consumed instead of writing
   * marks the editor no longer renders. Mutated at runtime through
   * {@link setEmphasisShortcutGateSuppressed} — the editors are never
   * recreated on a toggle flip, so storage is the live channel.
   */
  suppressed: boolean;
  /**
   * When the explanation toast last fired. A clinician reflexively hammering
   * the shortcut re-reads the toast already on screen instead of stacking a
   * column of copies.
   */
  lastToastAt: number;
  /**
   * When each mark's blocked attempt was last recorded. Holding a chord
   * auto-repeats keydown ~30×/s and TipTap shortcut handlers never see the
   * event (so `event.repeat` is unreachable) — the window caps a held chord
   * at one recorded attempt per second. Keyed per mark so a deliberate
   * different chord inside the window (⌘B then ⌘I) still records: the mark is
   * this event's only varying dimension, and auto-repeat always repeats the
   * same mark.
   */
  lastTrackAt: Partial<Record<SuppressedMark, number>>;
}

// Matches the toast's own display duration.
const TOAST_THROTTLE_MS = 5000;
// Long enough to swallow keyboard auto-repeat, short enough that deliberate
// separate presses still count.
const TRACK_THROTTLE_MS = 1000;

/**
 * Consumes Mod-B / Mod-I / Mod-U while the "Bold & italics" copy setting is
 * off in the note editor. Without the gate the shortcut writes an invisible
 * mark (the display suppresses bold/italic/underline), which reads as the
 * keypress silently doing nothing — instead, explain why in a toast and
 * record the attempt.
 *
 * Registered in every editor kit; the storage flag defaults to off and is
 * only ever raised for note editors, so document/template/context editors
 * keep their shortcuts live.
 */
export const EmphasisShortcutGate = Extension.create<
  Record<string, never>,
  EmphasisShortcutGateStorage
>({
  name: 'emphasisShortcutGate',

  // Above the mark extensions' default (100), so these bindings run before
  // Bold/Italic/Underline's own and can swallow the keypress.
  priority: 1000,

  addStorage() {
    return { suppressed: false, lastToastAt: 0, lastTrackAt: {} };
  },

  addKeyboardShortcuts() {
    const gate = (mark: SuppressedMark) => (): boolean => {
      if (!this.storage.suppressed) {
        // Fall through to the mark extension's own binding.
        return false;
      }
      const now = Date.now();
      if (now - this.storage.lastToastAt >= TOAST_THROTTLE_MS) {
        this.storage.lastToastAt = now;
        toast({
          tone: 'warning',
          title: formatMessageOutsideReact(
            emphasisSuppressionMessages.shortcutBlockedTitle
          ),
          description: formatMessageOutsideReact(
            emphasisSuppressionMessages.shortcutBlockedDescription
          ),
        });
      }
      // Taxonomy-standard `ui_interaction` (action `suppress`), not a bespoke
      // event name. Snowplow + Datadog only — new dispatch sites never reach
      // Amplitude. `element_name` is a static identifier, never content.
      if (now - (this.storage.lastTrackAt[mark] ?? 0) >= TRACK_THROTTLE_MS) {
        this.storage.lastTrackAt[mark] = now;
        track({
          event_name: TrackingEventName.UiInteraction,
          props: {
            action_type: 'suppress',
            element_type: 'control',
            element_name: `formatting_shortcut_${mark}`,
            session_tab: 'note',
          },
          options: { amplitude: false },
        });
      }
      return true;
    };

    // The mark extensions bind both cases (Shift/Caps Lock), so the gate must
    // cover both too or the capitalised chord slips through.
    return {
      'Mod-b': gate('bold'),
      'Mod-B': gate('bold'),
      'Mod-i': gate('italic'),
      'Mod-I': gate('italic'),
      'Mod-u': gate('underline'),
      'Mod-U': gate('underline'),
    };
  },
});

/**
 * Raise or clear the gate on a live editor. Safe against editors that don't
 * register the extension (the excluded-extensions paths) and against null
 * editors during mount.
 */
export function setEmphasisShortcutGateSuppressed(
  editor: Editor | null | undefined,
  suppressed: boolean
): void {
  const storage = (
    editor?.storage as Record<string, EmphasisShortcutGateStorage | undefined> | undefined
  )?.[EmphasisShortcutGate.name];
  if (storage) {
    storage.suppressed = suppressed;
  }
}
